/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Pie } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// StatsPieChart configurations
import configs from "./configs";

const BATTERY_THRESHOLD = 25;

function processStatsData(data, data_type) {

  var stats = {
    full: 0,
    healthy: 0,
    low: 0,
  }

  var data = data.filter(row => row.active);

  switch (data_type) {
    case 'battery': //min 0, max 100
      data.forEach((device) => {
        if (device.battery > 75) {
          stats.full++;
        }
        else if (device.battery > 35) {
          stats.healthy++;
        }
        else if (device.battery <= BATTERY_THRESHOLD) {
          stats.low++;
        }
      });
      break;
    case 'rssi': //min -51, max -113
      data.forEach((device) => {
        if (device.rssi > -70) {
          stats.full++;
        }
        else if (device.rssi > -85) {
          stats.healthy++;
        }
        else if (device.rssi <= -85) {
          stats.low++;
        }
      });
      break;
    case 'humidity': //min 0, max 100
      data.forEach((device) => {
        if (device.humidity > 80) {
          stats.low++;
        }
        else if (device.humidity > 55) {
          stats.healthy++;
        }
        else if (device.humidity <= 55) {
          stats.full++;
        }
      });
      break;
    case 'temperature': //min -20, max 70
      data.forEach((device) => {
        if (device.temperature > 60 || device.temperature < -5) {
          stats.low++;
        }
        else if (device.temperature > 45 || device.temperature < 5) {
          stats.healthy++;
        }
        else if (device.temperature <= 45 || device.temperature >= 5) {
          stats.full++;
        }
      });
      break;
  }

  return Object.values(stats);
}

// Setting default values for the props of StatsPieChart
function StatsPieChart({ title = "", height = '100%', devices, data_type }) {

  var stats = processStatsData(devices, data_type);

  var uptime_chart_settings = {
    labels: ["Full", "Healthy", "Low"],
    datasets: {
      label: { title },
      backgroundColors: ["success", "warning", "error"],
      data: stats
    }
  }

  const { data, options } = configs(uptime_chart_settings.labels || [], uptime_chart_settings.datasets || {});

  return (
    <Card>
      <SoftBox p={2}>
        <SoftBox>
          <SoftBox display={"flex"} justifyContent={"center"} alignItems={"center"} textAlign={"center"} mb={1}>
            <SoftTypography variant="h6">{title}</SoftTypography>
          </SoftBox>
        </SoftBox>
        {useMemo(
          () => (
            <SoftBox height={height}>
              <Pie data={data} options={options} />
            </SoftBox>
          ),
          [uptime_chart_settings, height]
        )}
      </SoftBox>
    </Card>
  )
}

// Typechecking props for the StatsPieChart
StatsPieChart.propTypes = {
  title: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  devices: PropTypes.array,
  data_type: PropTypes.string.isRequired
};

export default StatsPieChart;
